/**
 * 向导式测算流程（C 方案）
 *
 * 4 步：score → intent → computing → result
 */
import { useEffect, useState } from 'react'
import { useApp } from '@/store/useApp'
import { track } from '@/lib/track'
import { SCHOOL_NAMES, type CalcResult, type SchoolName, type YearPolicy } from '@/domain/types'

interface Props {
  policy: YearPolicy
  result: CalcResult | null
}

const STEP_INDEX = { score: 1, intent: 2, computing: 3 } as const

export function Wizard({ policy }: Props) {
  const { wizardStep, setWizardStep, score, setScore, intentSchool, setIntent } = useApp()

  const stepIdx = STEP_INDEX[wizardStep as 'score' | 'intent' | 'computing'] ?? 1
  const total = 3

  return (
    <div className="min-h-screen flex flex-col">
      <div className="container-mobile pt-10 pb-4">
        <button
          onClick={() => setWizardStep('idle')}
          className="text-ink-500 text-sm active:opacity-70"
        >
          ← 退出测算
        </button>
        <ProgressDots current={stepIdx} total={total} />
      </div>

      <div className="container-mobile flex-1 flex flex-col">
        {wizardStep === 'score' && (
          <StepScore
            initialScore={score}
            policy={policy}
            onNext={(s) => {
              setScore(s)
              setWizardStep('intent')
            }}
          />
        )}
        {wizardStep === 'intent' && (
          <StepIntent
            initialIntent={intentSchool}
            onPrev={() => setWizardStep('score')}
            onNext={(s) => {
              setIntent(s)
              // 记录「分数 + 意向学校」：用户确认意向的此刻分数已就绪，
              // 是校准报名偏好矩阵最干净的信号（不依赖用户后续是否点转化卡）。
              track('intent_select', {
                score,
                intent: s,
                year: policy.year
              })
              setWizardStep('computing')
            }}
          />
        )}
        {wizardStep === 'computing' && (
          <StepComputing
            year={policy.year}
            onDone={() => setWizardStep('result')}
          />
        )}
      </div>
    </div>
  )
}

function ProgressDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-2 mt-6">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={
            'h-2 rounded-full transition-all ' +
            (i + 1 === current ? 'w-8 bg-brand-600' : i + 1 < current ? 'w-2 bg-brand-300' : 'w-2 bg-ink-200')
          }
        />
      ))}
      <span className="ml-2 text-ink-400 text-sm">第 {current} 步，共 {total} 步</span>
    </div>
  )
}

// ────────── 步 1：分数输入 ──────────
function StepScore({
  initialScore,
  policy,
  onNext
}: {
  initialScore: number | null
  policy: YearPolicy
  onNext: (score: number) => void
}) {
  const [text, setText] = useState(initialScore === null ? '' : String(initialScore))
  const n = parseInt(text, 10)
  const valid = Number.isFinite(n) && n >= 200 && n <= 750

  return (
    <div className="py-6">
      <h1 className="text-h1 text-ink-900 mb-3">第一步</h1>
      <p className="text-h2 text-ink-700 mb-8">输入孩子的中考成绩</p>

      <input
        type="number"
        inputMode="numeric"
        pattern="[0-9]*"
        value={text}
        onChange={(e) => setText(e.target.value.replace(/\D/g, '').slice(0, 4))}
        onKeyDown={(e) => e.key === 'Enter' && valid && onNext(n)}
        placeholder="例如 558"
        autoFocus
        className="w-full text-[60px] leading-none font-light text-ink-900 px-4 py-6 rounded-2xl
          border-2 border-ink-200 bg-white
          focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400
          tabular-nums text-center"
      />

      <div className="mt-3 text-ink-500 text-sm leading-relaxed">
        分数仅在本机浏览器计算，不上传任何服务器。
        <br />
        参考年份：<span className="text-ink-700">{policy.year}</span>
        （资格线 {policy.qualification_line} · 前300线 {policy.top300_line}）
      </div>

      <div className="mt-auto pt-12">
        <button
          onClick={() => valid && onNext(n)}
          disabled={!valid}
          className="btn-primary w-full py-4 text-h3 disabled:opacity-40"
        >
          下一步 →
        </button>
      </div>
    </div>
  )
}

// ────────── 步 2：意向选择 ──────────
function StepIntent({
  initialIntent,
  onPrev,
  onNext
}: {
  initialIntent: SchoolName | null
  onPrev: () => void
  onNext: (s: SchoolName | null) => void
}) {
  const [picked, setPicked] = useState<SchoolName | null | 'none'>(initialIntent ?? null)

  return (
    <div className="py-6">
      <h1 className="text-h1 text-ink-900 mb-3">第二步</h1>
      <p className="text-h2 text-ink-700 mb-6">在测算之前，你最想让孩子去哪所学校？</p>
      <p className="text-ink-500 text-sm mb-6 leading-relaxed">
        这一步不影响分数计算，帮助我们了解今年家长的志愿偏好趋势
        （<span className="text-ink-700">不含你的分数</span>，完全匿名）。
        <br />
        参与的家长越多，模型对今年热度的判断就越准。
      </p>

      <div className="space-y-3 mb-8">
        {SCHOOL_NAMES.map((s) => (
          <button
            key={s}
            onClick={() => setPicked(s)}
            className={
              'w-full text-left px-5 py-4 rounded-2xl border-2 transition active:scale-[0.99] ' +
              (picked === s
                ? 'border-brand-500 bg-brand-50 text-brand-900'
                : 'border-ink-200 bg-white text-ink-800')
            }
          >
            <div className="text-h3">{s}</div>
          </button>
        ))}
        <button
          onClick={() => setPicked('none')}
          className={
            'w-full text-left px-5 py-4 rounded-2xl border-2 transition active:scale-[0.99] ' +
            (picked === 'none'
              ? 'border-brand-500 bg-brand-50 text-brand-900'
              : 'border-ink-200 bg-white text-ink-800')
          }
        >
          <div className="text-h3 text-ink-600">还没想好（看完结果再决定）</div>
        </button>
      </div>

      <div className="flex gap-3">
        <button onClick={onPrev} className="btn-ghost flex-1 py-4">
          ← 上一步
        </button>
        <button
          onClick={() => onNext(picked === 'none' ? null : (picked as SchoolName | null))}
          disabled={picked === null}
          className="btn-primary flex-[2] py-4 disabled:opacity-40"
        >
          开始测算 →
        </button>
      </div>
    </div>
  )
}

// ────────── 步 3：计算中（专业感动画） ──────────
function StepComputing({ year, onDone }: { year: number; onDone: () => void }) {
  const [progress, setProgress] = useState(0)
  const steps = [
    `读取邹城 ${year} 官方分段数据`,
    '定位你在所在分数段的顺位',
    '估算五校今年的报名热度',
    '模拟 200 种报名情境',
    '计算「冲 / 稳 / 保」区间',
    '生成你的专属报告'
  ]

  useEffect(() => {
    const total = 3000
    const tick = 30
    const inc = (tick / total) * 100
    const id = setInterval(() => {
      setProgress((p) => {
        const next = p + inc + Math.random() * 0.5
        if (next >= 100) {
          clearInterval(id)
          setTimeout(onDone, 200)
          return 100
        }
        return next
      })
    }, tick)
    return () => clearInterval(id)
  }, [onDone])

  const currentStep = Math.min(steps.length - 1, Math.floor((progress / 100) * steps.length))

  return (
    <div className="py-10">
      <div className="text-ink-500 text-sm mb-2">ZSM-1.1 自适应密度修匀模型</div>
      <h1 className="text-h1 text-ink-900 mb-8">正在为你计算…</h1>

      <div className="w-full h-3 bg-ink-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-brand-400 to-brand-700 transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="text-ink-600 text-right mt-2 tabular-nums">{Math.round(progress)}%</div>

      <div className="mt-8 space-y-2">
        {steps.map((s, i) => (
          <div
            key={s}
            className={
              'flex items-center gap-3 transition ' +
              (i < currentStep
                ? 'text-ink-700'
                : i === currentStep
                  ? 'text-brand-700'
                  : 'text-ink-300')
            }
          >
            <span className="w-5 inline-block">
              {i < currentStep ? '✓' : i === currentStep ? '→' : '·'}
            </span>
            <span>{s}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
